// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ColorStyleWidth } from '@shared/commons/DynProperty';
import { PenStyle } from '@shared/commons/Graphics';
import { Resources } from '@shared/localizations/Resources';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { TerceraChartBaseCrossHairRenderer } from './Renderers/TerceraChartBaseCrossHairRenderer';
import { LineStyle } from './Renderers/TerceraChartStyledLineRenderer';
import { TerceraChartStyledVerticalLineRenderer } from './Renderers/TerceraChartStyledVerticalLineRenderer';
import { TerceraChartAggregatedXYSeries, TerceraChartXYSeries, XYSeriesData } from './Series/TerceraChartXYSeries';
import { TerceraXYChart } from './TerceraXYChart';
import { ChartZoomRendererButtons } from './Renderers/TerceraChartZoomRenderer';
import { LayersEnum } from './Utils/LayersEnum';
export class OptionVolatilityLabChart extends TerceraXYChart {
    constructor() {
        super(...arguments);
        this._datesArray = [];
    }
    CreateMainWindow() {
        const mainWindow = super.CreateMainWindow();
        mainWindow.rightYScaleRenderer.Step = 0.01;
        mainWindow.rightYScaleRenderer.Precision = 2;
        mainWindow.rightYScaleRenderer.Format = ' %';
        return mainWindow;
    }
    CreateWindowsContainer() {
        const container = super.CreateWindowsContainer();
        container.zoomRenderer.CurrentVisibleButtons =
            ChartZoomRendererButtons.StepZoomIn | ChartZoomRendererButtons.StepZoomOut;
        return container;
    }
    CreateLeftYScaleRenderer() {
        return null;
    }
    InitializeRenderers() {
        super.InitializeRenderers();
        this._basePriceLineRenderer = new TerceraChartStyledVerticalLineRenderer(this);
        this.mainWindow.Renderers.push(this._basePriceLineRenderer);
        this.mainWindow.Renderers.push(new TerceraChartBaseCrossHairRenderer(this));
    }
    DrawNoData(gr) {
        this.NoDataMessage = Resources.getResource('panel.optionVolatilityLab.ChartNoData');
        super.DrawNoData(gr);
    }
    HasCorrectData() {
        const hasCorrectData = super.HasCorrectData();
        let hasVisibleLine = false;
        const lineRenderers = this.getLineRenderers();
        for (let i = 0; i < lineRenderers.length; i++) {
            if (lineRenderers[i].Visible) {
                hasVisibleLine = true;
                break;
            }
        }
        return hasCorrectData && hasVisibleLine;
    }
    ThemeChanged() {
        super.ThemeChanged();
        this._basePriceLineRenderer.ColorStyleWidth = new ColorStyleWidth(ThemeManager.colors.OptionMasterAnalyzerZeroLineColor, PenStyle.SimpleChart, 1);
    }
    set(chartData) {
        this._datesArray = [];
        for (const entry of chartData.seriesMapDataSeries) {
            const optionDataSeriesDate = entry[0];
            this._datesArray.push(optionDataSeriesDate);
        }
        this.updateBasePriceLine(chartData.underlierPrice);
        const series = this.createSeries(chartData);
        this.setSeries(series);
    }
    update(chartData) {
        this.updateBasePriceLine(chartData.underlierPrice);
        const series = this.createSeries(chartData);
        super.updateSeries(series);
        this.IsDirty(LayersEnum.All);
    }
    updateBasePriceLine(basePrice) {
        const basePriceLineSeries = new TerceraChartXYSeries();
        basePriceLineSeries.data.push(new XYSeriesData(basePrice, 0));
        this._basePriceLineRenderer.Series = basePriceLineSeries;
    }
    resetScales() {
        this.updateMinMaxXScaleBorders();
        this.updateMinMaxYScaleBorders();
    }
    setLineColor(date, color) {
        const lineRenderer = this.getLineRendererByIndex(this._datesArray.indexOf(date));
        if (!isNullOrUndefined(lineRenderer)) {
            lineRenderer.LineStyle = LineStyle.LineDots;
            lineRenderer.ColorStyleWidth = new ColorStyleWidth(color, PenStyle.SimpleChart, 2);
        }
        this.IsDirty(LayersEnum.All);
    }
    setSelectedLines(dates) {
        const lineRenderers = this.getLineRenderers();
        for (let i = 0; i < lineRenderers.length; i++) {
            const date = this._datesArray[i];
            const lineRenderer = lineRenderers[i];
            lineRenderer.isSelected = dates.includes(date);
        }
        this.IsDirty(LayersEnum.All);
    }
    createSeries(chartData) {
        const xScaleRenderer = this.windowsContainer.xScaleRenderer;
        xScaleRenderer.Step = chartData.priceStep;
        xScaleRenderer.Precision = chartData.pricePrecision;
        xScaleRenderer.Legend = chartData.xScaleLegend;
        const series = new TerceraChartAggregatedXYSeries();
        for (const entry of chartData.seriesMapDataSeries) {
            const optionDataSeries = entry[1];
            const optionsSeries = new TerceraChartXYSeries();
            for (let i = 0; i < optionDataSeries.length; i++) {
                optionsSeries.data.push(new XYSeriesData(optionDataSeries[i].x, optionDataSeries[i].y));
            }
            series.data.push(optionsSeries);
        }
        return series;
    }
}
